/**
 * Does a URL belong to a sidebar entry?
 *
 * The sidebar used to answer this with `pathname.includes(href)`, which is a
 * substring test rather than a path test. That quietly lights up the wrong row
 * whenever one route's path is a prefix of another's — `/challenges` matches
 * `/challenge-insights`, `/marketplace` matches `/marketplace-insights`, and
 * `/meditation` matched the old `/content-management/meditation`. The bug only
 * shows up as "two rows are highlighted", which is easy to live with and hard
 * to trace back to the matcher.
 *
 * A path belongs to an entry when it IS that path, or sits underneath it as a
 * whole segment — so `/perks/users` matches `/perks`, and `/perks-insights`
 * does not.
 */
export const isPathUnder = (pathname, href) => {
  if (!pathname || !href) return false;

  // Trailing slashes are not part of the comparison; `/perks/` is `/perks`.
  const path = pathname.replace(/\/+$/, '');
  const base = href.replace(/\/+$/, '');
  if (!base) return false;

  return path === base || path.startsWith(`${base}/`);
};

/**
 * The same question for an entry whose routes do not all sit under its href.
 * Elite Games' sidebar rows link to a game's first page but must stay lit on
 * the rest of it, and some of those pages are siblings rather than children
 * (`/game/guess-the-word-category` beside `/game/guess-the-word/subcategory`),
 * so the entry lists the prefixes it owns in `matchHrefs`.
 */
export const matchesEntry = (pathname, entry) => {
  const bases = entry?.matchHrefs?.length ? entry.matchHrefs : [entry?.href];
  return bases.some((base) => isPathUnder(pathname, base));
};

export default isPathUnder;
